import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import type { TypedStartListening } from '@reduxjs/toolkit';
import { monitoring } from 'common/services';
import {
  NatteringTelemetry,
  TelemetryContext,
  roundAttrs,
} from 'common/services/monitoring/telemetry/natteringTelemetry';
import { isFeatureEnabled } from 'common/utils';
import type { CombinedDispatch, RootState } from 'store';

import { broadcastStarted_WS } from '../actions/broadcast.actions';
import { joinRoom_WS, natteringActions, natteringStarted_WS, roomExit_WS } from '../actions/room.actions';
import { setActiveEventId } from '../eventSlice.actions';
import { selectNatteringRoundStartTime } from '../selectors';
import { classifyJoinPhase } from './classifyJoinPhase';

type AppStartListening = TypedStartListening<RootState, CombinedDispatch>;

export const natteringTelemetryListener = createListenerMiddleware();
const startListening = natteringTelemetryListener.startListening as AppStartListening;

/** JIT-only: skip everything when the flag is off, so the legacy modal path is not instrumented. */
const jitEnabled = () => isFeatureEnabled('JustInTimePermissions');

// Per-session join classification. A broadcast viewer always processes broadcastStarted_WS (it carries
// the broadcast access token), so this flag distinguishes "present during broadcast" from "joined into
// an in-progress nattering round". Reset on setActiveEventId (a fresh event in the same tab).
let sawBroadcast = false;
let joinClassified = false;

startListening({
  actionCreator: broadcastStarted_WS,
  effect: () => {
    if (!jitEnabled()) return;
    sawBroadcast = true;
  },
});

// Record the join phase once, on the first round start, as a neutral fact (no "late" judgement —
// broadcast resumes / early starts make that unreliable). A nattering-join is pinned to its round via
// joinedRoundStartTime so the round ordinal is derivable; broadcasts carry no global sequence so a
// broadcast-join is just 'broadcast'. Set for the whole event, cleared on setActiveEventId.
startListening({
  actionCreator: natteringStarted_WS,
  effect: (_action, { getState }) => {
    if (!jitEnabled()) return;
    // A new round means any previous room is no longer current — safety-net clear in case the round
    // ended via a path that didn't emit roomExit_WS / broadcastStarted_WS (timeout, network failure).
    monitoring.removeContext(TelemetryContext.room);

    if (joinClassified) return;
    joinClassified = true;
    const joinPhase = classifyJoinPhase(sawBroadcast);
    const joinedRoundStartTime = selectNatteringRoundStartTime(getState() as RootState);
    monitoring.setContext(TelemetryContext.joinContext, {
      joinPhase,
      ...(joinPhase === 'nattering' ? { joinedRoundStartTime } : {}),
    });
  },
});

// Authoritative "joined the pool".
startListening({
  actionCreator: natteringActions.joinNatteringModal.accepted,
  effect: (_action, { getState }) => {
    if (!jitEnabled()) return;
    const roundStartTime = selectNatteringRoundStartTime(getState() as RootState);
    monitoring.trackAction(NatteringTelemetry.poolOptIn, roundAttrs(roundStartTime));
  },
});

// Authoritative "left the pool".
startListening({
  actionCreator: natteringActions.joinNatteringModal.skipped,
  effect: (_action, { getState }) => {
    if (!jitEnabled()) return;
    const roundStartTime = selectNatteringRoundStartTime(getState() as RootState);
    monitoring.trackAction(NatteringTelemetry.poolSkip, roundAttrs(roundStartTime));
  },
});

// Tag the matched room onto subsequent actions.
startListening({
  actionCreator: joinRoom_WS,
  effect: ({ payload }) => {
    if (!jitEnabled()) return;
    monitoring.setContext(TelemetryContext.room, { roomId: payload.id });
  },
});

// Clear the room context when the user leaves the room or broadcast resumes (room no longer current).
startListening({
  matcher: isAnyOf(roomExit_WS, broadcastStarted_WS),
  effect: () => {
    if (!jitEnabled()) return;
    monitoring.removeContext(TelemetryContext.room);
  },
});

// New event in the same tab: reset the join classification and clear its context so it can't leak.
startListening({
  actionCreator: setActiveEventId,
  effect: () => {
    if (!jitEnabled()) return;
    sawBroadcast = false;
    joinClassified = false;
    monitoring.removeContext(TelemetryContext.joinContext);
  },
});
